/**
 * Cash Flow Form Component
 * Displays cash flow statement with Operating Activities, Investing Activities, and Free Cash Flow
 */
import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

const HelpTooltip = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle size={14} className="inline text-blue-600 cursor-help" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg bottom-full left-1/2 -translate-x-1/4 mb-1 pointer-events-none">
      {text}
    </div>
  </div>
);

HelpTooltip.propTypes = {
  text: PropTypes.string.isRequired
};

const OPERATING_ACTIVITIES_FIELDS = [
  {
    label: 'Net Income',
    field: 'profit_after_tax',
    help: 'Auto-populated from Income tab. Starting point for cash flow analysis. Already includes Interest and Tax expenses.',
    readonly: true
  },
  {
    label: 'Depreciation & Amortization (D&A)',
    field: 'depreciation',
    help: 'Auto-populated from Income tab. Added back because it is a non-cash expense.',
    readonly: true
  },
  {
    label: 'Change in Working Capital',
    field: 'working_capital_change',
    help: 'Auto-calculated from Balance Sheet: (Current Assets - Current Liabilities) this year minus previous year. Negative = cash outflow (increase in WC), Positive = cash inflow (decrease in WC).',
    readonly: true
  }
];

const INVESTING_ACTIVITIES_FIELDS = [
  {
    label: 'Capital Expenditures (CapEx)',
    field: 'capex',
    help: 'Cash spent on purchasing or upgrading fixed assets (property, equipment, technology). Enter as positive number for cash outflow.'
  }
];

const FREE_CASH_FLOW_FIELDS = [
  {
    label: 'Free Cash Flow (FCF)',
    field: 'free_cash_flow',
    help: 'Auto-calculated: Net Income + D&A - CapEx ± Working Capital Change. Cash available after maintaining/expanding the business.',
    readonly: true,
    bold: true
  }
];

const CashFlowForm = ({
  years,
  financialData,
  formatNumberWithCommas,
  handleInputChange
}) => {
  return (
    <div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="text-left p-3 border">Line Item</th>
            {years.map(y => <th key={y} className="p-3 border">{y}</th>)}
          </tr>
        </thead>
        <tbody>
          {/* OPERATING ACTIVITIES SECTION */}
          <tr className="bg-blue-100">
            <td colSpan={years.length + 1} className="p-2 border font-bold text-blue-900">
              CASH FLOW FROM OPERATING ACTIVITIES
            </td>
          </tr>
          {OPERATING_ACTIVITIES_FIELDS.map((item, idx) => (
            <tr key={item.field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="p-3 border font-medium">
                {item.label}
                {item.help && <HelpTooltip text={item.help} />}
                {item.readonly && <span className="ml-2 text-xs text-green-600">(Auto)</span>}
              </td>
              {years.map(year => (
                <td key={year} className="p-2 border">
                  <input
                    type="text"
                    value={formatNumberWithCommas(financialData[item.field][year])}
                    onChange={(e) => handleInputChange(item.field, year, e.target.value)}
                    className={`w-full px-2 py-1 border rounded ${
                      item.readonly ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="$0"
                    readOnly={item.readonly}
                    disabled={item.readonly}
                  />
                </td>
              ))}
            </tr>
          ))}

          {/* INVESTING ACTIVITIES SECTION */}
          <tr className="bg-green-100">
            <td colSpan={years.length + 1} className="p-2 border font-bold text-green-900">
              CASH FLOW FROM INVESTING ACTIVITIES
            </td>
          </tr>
          {INVESTING_ACTIVITIES_FIELDS.map((item, idx) => (
            <tr key={item.field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="p-3 border font-medium">
                {item.label}
                {item.help && <HelpTooltip text={item.help} />}
              </td>
              {years.map(year => (
                <td key={year} className="p-2 border">
                  <input
                    type="text"
                    value={formatNumberWithCommas(financialData[item.field][year])}
                    onChange={(e) => handleInputChange(item.field, year, e.target.value)}
                    className="w-full px-2 py-1 border rounded"
                    placeholder="$0"
                  />
                </td>
              ))}
            </tr>
          ))}

          {/* FREE CASH FLOW SECTION */}
          <tr className="bg-purple-100">
            <td colSpan={years.length + 1} className="p-2 border font-bold text-purple-900">
              FREE CASH FLOW
            </td>
          </tr>
          {FREE_CASH_FLOW_FIELDS.map((item, idx) => (
            <tr key={item.field} className="bg-purple-50">
              <td className={`p-3 border font-medium ${item.bold ? 'font-bold' : ''}`}>
                {item.label}
                {item.help && <HelpTooltip text={item.help} />}
                {item.readonly && <span className="ml-2 text-xs text-green-600">(Auto)</span>}
              </td>
              {years.map(year => (
                <td key={year} className="p-2 border bg-purple-50">
                  <input
                    type="text"
                    value={formatNumberWithCommas(financialData[item.field][year])}
                    onChange={(e) => handleInputChange(item.field, year, e.target.value)}
                    className="w-full px-2 py-1 border rounded bg-gray-100 cursor-not-allowed font-semibold"
                    placeholder="$0"
                    readOnly={item.readonly}
                    disabled={item.readonly}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

CashFlowForm.propTypes = {
  years: PropTypes.arrayOf(PropTypes.number).isRequired,
  financialData: PropTypes.object.isRequired,
  formatNumberWithCommas: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired
};

export default CashFlowForm;
